import { getCountryName } from "@/features/flight-estimator/_logic/iso-country-utils";

import type { AirportRecord } from "./places";
import { fold, matchPlace, type buildPlaceIndex } from "./places";

/** How many airports a search shows; past that the planner types more. */
export const AIRPORT_OPTIONS_SHOWN = 8;

/** One airport the picker offers. */
export interface AirportOption {
  code: string;
  /** "SFO · San Francisco International". */
  label: string;
  /** "San Francisco, US". */
  sublabel: string;
}

const toOption = (airport: AirportRecord): AirportOption => ({
  code: airport.iata,
  label: `${airport.iata} · ${airport.name}`,
  sublabel: `${airport.city}, ${getCountryName(airport.country) ?? airport.country}`,
});

/**
 * The airports that serve a home city, the one fares are priced from first (its primary, then
 * metro airports: Tokyo is HND then NRT; San Francisco is SFO, OAK, SJC).
 * @param city - The home city as the workspace names it.
 * @param index - The place index.
 * @param airports - The airport catalog, for names.
 * @returns The city's airports, empty when the city isn't a known place.
 * @throws Never.
 */
export const cityAirports = (
  city: string,
  index: ReturnType<typeof buildPlaceIndex>,
  airports: readonly AirportRecord[]
): AirportOption[] => {
  const place = matchPlace(city, index);
  if (!place) return [];
  const byCode = new Map(airports.map((airport) => [airport.iata, airport]));
  return place.codes.flatMap((code) => {
    const airport = byCode.get(code);
    return airport ? [toOption(airport)] : [];
  });
};

/**
 * Airports matching what the planner typed, for flying from somewhere other than the city's own:
 * an exact code first, then codes, cities and airport names starting with the text, then names
 * containing it; large airports before medium ones at the same rank.
 * @param query - What was typed; under two characters matches nothing.
 * @param airports - The airport catalog.
 * @returns At most {@link AIRPORT_OPTIONS_SHOWN} airports, best first.
 * @throws Never.
 */
export const searchAirports = (query: string, airports: readonly AirportRecord[]): AirportOption[] => {
  const q = fold(query);
  if (q.length < 2) return [];
  const rank = (airport: AirportRecord): number => {
    const code = airport.iata.toLowerCase();
    const city = fold(airport.city);
    const name = fold(airport.name);
    if (code === q) return 4;
    if (code.startsWith(q) || city.startsWith(q) || name.startsWith(q)) return 3;
    if (city.split(" ").some((word) => word.startsWith(q)) || name.split(" ").some((word) => word.startsWith(q)))
      return 2;
    if (airport.keywords.some((keyword) => fold(keyword).startsWith(q))) return 1;
    return 0;
  };
  return airports
    .map((airport) => ({ airport, rank: rank(airport) }))
    .filter(({ rank: score }) => score > 0)
    .sort(
      (a, b) =>
        b.rank - a.rank ||
        Number(b.airport.type === "large_airport") - Number(a.airport.type === "large_airport") ||
        a.airport.iata.localeCompare(b.airport.iata)
    )
    .slice(0, AIRPORT_OPTIONS_SHOWN)
    .map(({ airport }) => toOption(airport));
};

/**
 * Why a picked airport is flagged: it serves another city, so fares from it aren't the home
 * city's. Null for one of the city's own airports or an unknown code.
 * @param code - The picked IATA code.
 * @param city - The home city.
 * @param own - The city's own airports, from {@link cityAirports}.
 * @param airports - The airport catalog.
 * @returns The note ("HNL is Honolulu's airport, not Tokyo's"), or null.
 * @throws Never.
 */
export const farAirportNote = (
  code: string,
  city: string,
  own: readonly AirportOption[],
  airports: readonly AirportRecord[]
): string | null => {
  if (own.length === 0 || own.some((option) => option.code === code)) return null;
  const airport = airports.find((entry) => entry.iata === code);
  if (!airport) return null;
  return `${code} is ${airport.city}'s airport, not ${city}'s`;
};
